import React from "react";

interface State {
  hasError: boolean;
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Catches uncaught render-time errors in descendants (e.g. a malformed AI
 * response crashing MermaidRenderer/MarkmapRenderer) and shows a recover-able
 * fallback instead of the React white-screen-of-death (audit C9).
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
          color: "#fff",
        }}
      >
        <h2 style={{ marginBottom: 12, fontSize: 22 }}>Something went wrong</h2>
        <p style={{ marginBottom: 24, opacity: 0.7, maxWidth: 420 }}>
          We hit an unexpected error rendering this page. Please reload — your
          session is safe.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            padding: "10px 28px",
            borderRadius: 8,
            background: "#3af062",
            color: "#000",
            border: "none",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
